import React from 'react';
import Dialog, {DialogProps} from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText, {DialogContentTextProps} from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Box, {BoxProps} from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Message from './Message';
import Button, {ButtonProps} from '../common/button/Button';
import useStyles from './ConfirmDialog.styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import useTheme from '@material-ui/core/styles/useTheme';

export interface ConfirmDialogProps extends Omit<DialogProps, 'onClose' | 'title'> {
    /**
     * Customize Props
     **/
    className?: string;
    title?: React.ReactNode;
    html?: boolean; // Parse text labels as HTML
    boxProps?: BoxProps; // Dialog Wrapper Props
    /**
     * Required Props
     **/
    open: boolean;
    onClose: (e?) => void;
    /**
     * Content Props
     **/
    dialogContentTextProps?: DialogContentTextProps<'div'>;
    /**
     * Cancel Button Props
     **/
    cancel?: React.ReactNode;
    onCancel?: (e?) => void;
    cancelRedirectPath?: string;
    cancelButtonProps?: Omit<ButtonProps, 'to' | 'onClick'>;
    /**
     * Submit Button Props
     **/
    submit?: React.ReactNode;
    onSubmit: (e?) => void;
    submitRedirectPath?: string;
    submitButtonProps?: Omit<ButtonProps, 'to' | 'onClick'>;
}

const CODES = {
    title: 'epic.affiliate.closeAccount.areYouSure.v2',
    submit: 'epic.affiliate.common.submit',
    cancel: 'epic.affiliate.common.cancel'
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    title,
    submit,
    submitRedirectPath,
    cancel,
    cancelRedirectPath,
    open = false,
    html = false,
    onClose = () => {},
    onCancel = () => {},
    onSubmit = () => {},
    className,
    cancelButtonProps,
    submitButtonProps,
    dialogContentTextProps,
    boxProps,
    children,
    ...rest
}) => {
    const styles = useStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const renderContent = value => {
        if (typeof value !== 'string') return value;

        if (html) {
            return <span dangerouslySetInnerHTML={{__html: value}} />;
        } else {
            return <Message code={value} />;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            className={className}
            classes={{paper: styles.dialogRoot}}
            fullScreen={fullScreen}
            {...rest}>
            <Box display="flex" flexDirection="column" height="100%" {...boxProps}>
                <IconButton
                    classes={{root: styles.closeIconRoot}}
                    aria-label="close"
                    size="medium"
                    onClick={onClose}>
                    <CloseIcon fontSize="large" />
                </IconButton>

                <DialogTitle classes={{root: styles.dialogTitleRoot}}>
                    <Typography variant="h2" align="center">
                        {renderContent(title || CODES.title)}
                    </Typography>
                </DialogTitle>

                <DialogContent classes={{root: styles.dialogContentRoot}}>
                    <DialogContentText
                        component="div"
                        classes={{root: styles.dialogContentTextRoot}}
                        {...dialogContentTextProps}>
                        {children}
                    </DialogContentText>
                </DialogContent>

                <DialogActions classes={{root: styles.dialogActionsRoot}}>
                    <Button
                        fullWidth
                        to={submitRedirectPath}
                        size="large"
                        color="primary"
                        type="submit"
                        onClick={e => {
                            onSubmit(e);
                            onClose(e);
                        }}
                        {...submitButtonProps}>
                        {renderContent(submit || CODES.submit)}
                    </Button>

                    <Button
                        fullWidth
                        to={cancelRedirectPath}
                        size="large"
                        color="secondary"
                        onClick={e => {
                            onCancel(e);
                            onClose(e);
                        }}
                        {...cancelButtonProps}>
                        {renderContent(cancel || CODES.cancel)}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export default ConfirmDialog;
